import React, { useEffect, useRef, useState, useContext } from "react";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { prProfileUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import ListRow from "../../profiles/ListRow";
import Hashtags from "../../hashtags";
import CompanyPersonRowSkeleton from "./company_person_row_skeleton";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";
import { CompanyContext } from "../data/company_context_management";

const PER_PAGE = 12;
const MAX_HASHTAGS = 3;
const FIRST_PAGE_PLACEHOLDERS = 6;
const NEXT_PAGE_PLACEHOLDERS = 3;

// These people live on the PR profile index, so that is the filtered listing their chips reach; the
// model-generic <Hashtags> has no way to know which index a page family searches.
function prProfileHashtagUrl(hashtag) {
  return `/pr_profiles?hashtag=${encodeURIComponent(hashtag.hashtag_name)}`;
}

// The dividers belong to the list, not the row: every row but the last carries the rule beneath it.
function dividerClasses(index, count) {
  return `py-2${index < count - 1 ? " border-bottom" : ""}`;
}

// A PR agency's own PR team, as a self-contained section card: it owns its <SectionCard> so it can
// collapse away entirely when there is nobody to show. Viewers never get an empty card; an editor
// keeps it (with a one-line empty state) because the "Add a section" dropdown only lists sections
// that are switched OFF — a switched-on but empty section would otherwise leave its own show/hide
// switch unreachable.
function CompanyPrTeam() {
  const { companyId, company, viewerContext, setOpenEditModal } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const configured = company.displayType === "pr_firm" && !!company.page_config?.show_prs;

  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [prProfiles, setPrProfiles] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // The provider hands out a fresh `company` object on every authoritative read, so its identity --
  // not the company id alone -- is this section's refresh signal. It restarts pagination from an
  // empty list, so the rows on screen can never be a mix of pages fetched before and after the
  // refresh, and it is a dependency of the fetch below, so a section already sitting on page 1
  // refetches too. Taking the new signal during the render that first sees it, rather than in an
  // effect, keeps a refresh to a single request. Dropping `hasMore` matters as much as the rows:
  // it takes the old scroll trigger out of the page, so a trigger sitting in the viewport cannot
  // advance pagination off the pre-refresh list while page 1 is still in flight.
  const [fetchContext, setFetchContext] = useState({ companyId, company });
  if (fetchContext.companyId !== companyId || fetchContext.company !== company) {
    setFetchContext({ companyId, company });
    setPrProfiles([]);
    setHasMore(false);
    setPage(1);
    // Straight to the loading skeleton: without this the commit that empties the list would paint
    // one frame of the empty state (or collapse the card entirely for a viewer) before the fetch
    // effect gets to run.
    setIsLoading(true);
  }

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  // Effect to track infinite scrolling intersection observer event, which increments page. Each
  // trigger is consumed exactly once, and only while there is more to fetch: the observer can
  // still deliver a queued intersection for a trigger node a refresh just unmounted, and paging
  // forward off that would append later pages onto a list that is being refetched from page 1.
  const consumedTriggerRef = useRef(0);
  useEffect(() => {
    if (lastTriggeredAt <= consumedTriggerRef.current) return;

    consumedTriggerRef.current = lastTriggeredAt;
    if (hasMore) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt, hasMore]);

  // Switching the section off takes the scroll trigger below out of the page while the infinite
  // scroll observer is still watching the old node, and the observer only re-attaches when
  // `hasMore` changes. Emptying the section stops pagination, so switching it back on fetches page
  // 1 afresh and flips `hasMore` back on against the new trigger.
  useEffect(() => {
    if (configured) return;

    setPrProfiles([]);
    setHasMore(false);
    setPage(1);
  }, [configured]);

  // Retrieve a company's PR team from the API and track the page number
  useEffect(() => {
    let componentIsMounted = true;
    const fetchPrProfiles = async () => {
      try {
        if (companyId && configured) {
          if (page === 1) setIsLoading(true);
          qahoy.track("Companies#getPrProfiles");

          const params = {
            companyPageVisible: true,
            page,
            per_page: PER_PAGE,
          };

          const response = await qapi.companyJobs.getAllProfilesByCompany(companyId, params);
          if (componentIsMounted && response.data) {
            const transformedProfiles = response.data.map((item) => ({
              company_job_id: item.id,
              company_hidden: item.company_hidden,
              company_highlighted: item.company_highlighted,
              description: item.person_description,
              id: item.person_id,
              name: item.person_name,
              profile_image_url: item.person_profile_image_url,
              slug: item.person_slug,
              migrate_id: item.person_migrate_id,
              featured_title: item.title,
            }));
            // Page 1 replaces instead of appending: it is also what a restart re-fetches, and
            // appending there would stack a second copy of the first page onto the rows on screen.
            setPrProfiles((prevRecords) =>
              page === 1 ? transformedProfiles : [...prevRecords, ...transformedProfiles],
            );
            setHasMore(response.remainingRows > 0);
          }
          if (componentIsMounted) setIsLoading(false);
        }
      } catch (error) {
        if (componentIsMounted) setAsyncError(error);
      }
    };
    fetchPrProfiles();

    // Cleanup function to avoid the "Can't perform a React state update on an unmounted component"/memory leak problem
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, configured, page, fetchContext]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!configured) return null;

  const isEmpty = !isLoading && prProfiles.length === 0;
  if (isEmpty && !editable) return null;

  return (
    <SectionCard
      id="company-section-pr-team"
      title="Our PR Team"
      editable={editable}
      onEdit={() => setOpenEditModal("PR_TEAM")}
      editButtonId="modal-button-PR_TEAM"
    >
      {isEmpty ? (
        <div className="text-muted">No PR team members added yet.</div>
      ) : (
        <div className="col px-0">
          <ul className="list-unstyled mb-0">
            {isLoading &&
              Array.from({ length: FIRST_PAGE_PLACEHOLDERS }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li className={dividerClasses(index, FIRST_PAGE_PLACEHOLDERS)} key={`pr-placeholder-${index}`}>
                  <CompanyPersonRowSkeleton />
                </li>
              ))}
            {!isLoading &&
              prProfiles.map((prProfile, index) => (
                <li className={dividerClasses(index, prProfiles.length)} key={prProfile.id}>
                  <ListRow
                    href={prProfileUrl({ slug: prProfile.slug, migrate_id: prProfile.migrate_id })}
                    imageUrl={prProfile.profile_image_url}
                    title={prProfile.name}
                    subtitleTitle={prProfile.featured_title}
                    tags={
                      <Hashtags
                        clickEventName="PrProfiles/PrProfileCard#clickHashtag"
                        maxHashtags={MAX_HASHTAGS}
                        profileId={prProfile.id}
                        hashtagUrlResolver={prProfileHashtagUrl}
                        className="d-flex flex-wrap gap-1 mt-1"
                      />
                    }
                  />
                </li>
              ))}
          </ul>
          {hasMore && (
            <div ref={scrollTriggerRef}>
              <ul className="list-unstyled mb-0">
                {Array.from({ length: NEXT_PAGE_PLACEHOLDERS }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li className={dividerClasses(index, NEXT_PAGE_PLACEHOLDERS)} key={`pr-next-placeholder-${index}`}>
                    <CompanyPersonRowSkeleton />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export default CompanyPrTeam;
